/**
 * YouTube IFrame Player を Solid のライフサイクルに載せる（要件定義 3.3）。
 *
 * YouTube 標準のコントロール（再生・一時停止・シーク・音量・帰属表示）は隠さない。
 * 834CH 側は videoId と再生開始位置を渡すだけで、操作そのものには介入しない。
 */

import { type Component, createEffect, onCleanup, onMount, untrack } from 'solid-js'
import { loadYouTubeApi } from '~/lib/youtube'

export type PlayerState = 'unstarted' | 'ended' | 'playing' | 'paused' | 'buffering' | 'cued'

const STATE_BY_CODE: Record<number, PlayerState> = {
  [-1]: 'unstarted',
  0: 'ended',
  1: 'playing',
  2: 'paused',
  3: 'buffering',
  5: 'cued',
}

export type YouTubePlayerProps = {
  videoId: string
  /** 再生開始位置（秒）。videoId が変わったときだけ使う */
  startSeconds: number
  /**
   * 番組が切り替わったときに自動で再生するか。
   * ユーザーが明示的に一時停止している場合は false にして、
   * 動画だけ差し替えて再生は再開しない（要件定義 3.5）。
   */
  autoplay?: boolean
  /** Player の生成が終わったら呼ばれる。親から seek などを行うために使う */
  onReady?: (player: YT.Player) => void
  onStateChange?: (state: PlayerState) => void
  onError?: (code: number) => void
}

export const YouTubePlayer: Component<YouTubePlayerProps> = (props) => {
  let container!: HTMLDivElement
  let player: YT.Player | undefined
  let disposed = false
  /** 生成時に指定済みの videoId。同じ動画で作り直さないために持つ */
  let loadedVideoId: string | undefined

  onMount(async () => {
    const api = await loadYouTubeApi().catch((error: unknown) => {
      console.error(error)
      return null
    })
    if (!api || disposed) return

    loadedVideoId = untrack(() => props.videoId)
    player = new api.Player(container, {
      // API は渡した要素を iframe に置き換えるので、寸法はここで指定する
      // （既定は 640x390 の固定値で、広げた枠のなかで小さいままになる）
      width: '100%',
      height: '100%',
      videoId: loadedVideoId,
      playerVars: {
        // 標準 UI はそのまま使う。controls / modestbranding で隠さない（要件定義 14 章）
        playsinline: 1,
        start: Math.max(0, Math.floor(untrack(() => props.startSeconds))),
        origin: window.location.origin,
      },
      events: {
        onReady: (event) => props.onReady?.(event.target),
        onStateChange: (event) => {
          const state = STATE_BY_CODE[event.data]
          if (state) props.onStateChange?.(state)
        },
        onError: (event) => props.onError?.(event.data),
      },
    })
  })

  // 番組が切り替わったら動画を差し替える（Player は作り直さない）
  createEffect(() => {
    const videoId = props.videoId
    if (!player || videoId === loadedVideoId) return
    loadedVideoId = videoId
    // 再生開始位置は「番組が切り替わった瞬間の値」だけを使う。
    // 1 秒ごとに進む値なので、追跡すると毎秒読み込み直してしまう
    const request = {
      videoId,
      startSeconds: Math.max(0, Math.floor(untrack(() => props.startSeconds))),
    }
    if (untrack(() => props.autoplay) === false) {
      // 読み込むだけで再生はしない
      player.cueVideoById(request)
    } else {
      player.loadVideoById(request)
    }
  })

  onCleanup(() => {
    disposed = true
    player?.destroy()
    player = undefined
  })

  return <div ref={container} class="size-full" />
}
